import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { getClientLooks, respondToApproval, createDirectionNote, deleteClientLooks } from '../../api/clientPortal';
import { useClientTheme } from '../../contexts/ClientThemeContext';
import { Spinner, ErrorBanner } from './ClientDashboardPage';
import { ThumbsUp, ThumbsDown, Pencil, Trash2 } from 'lucide-react';

const GOLD = '#C9A84C';
const TAGS = ['color', 'silhouette', 'fabric', 'reference', 'fit', 'finish'];

const STATUS_META = {
  pending:        { label: 'Pending',        dot: GOLD },
  approved:       { label: 'Approved',       dot: '#4ade80' },
  rejected:       { label: 'Rejected',       dot: '#f87171' },
  needs_revision: { label: 'Needs Revision', dot: '#818cf8' },
};

function StatusDot({ status }) {
  const m = STATUS_META[status] || { dot: '#888' };
  return <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: m.dot, flexShrink: 0 }} />;
}

export default function ClientLooksPage() {
  const { theme: T } = useClientTheme();
  const isLight = T.id === 'light';

  const { data, isLoading, isError, error, refetch } = useQuery(['client-looks'], getClientLooks);
  const albums = useMemo(() => (Array.isArray(data?.albums) ? data.albums : []), [data]);

  const [submittingId, setSubmittingId] = useState('');
  const [noteOpenGroup, setNoteOpenGroup] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [noteTags, setNoteTags] = useState([]);
  const [pageError, setPageError] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [hoveredLook, setHoveredLook] = useState('');
  const [lightbox, setLightbox] = useState(null); // { url, label, look }
  const [deletingGroupId, setDeletingGroupId] = useState('');
  const [confirmDeleteGroupId, setConfirmDeleteGroupId] = useState('');

  const respond = async (approvalId, status) => {
    setPageError('');
    setSubmittingId(approvalId);
    try {
      await respondToApproval(approvalId, { status });
      await refetch();
    } catch (err) {
      setPageError(err?.response?.data?.error || 'Failed to submit response.');
    } finally {
      setSubmittingId('');
    }
  };

  const deleteGroup = async (group) => {
    const ids = [
      group.reference?.latestApprovalId,
      ...group.generated.map(l => l.latestApprovalId),
    ].filter(Boolean).map(String);
    setDeletingGroupId(group.shareGroupId);
    setPageError('');
    try {
      await deleteClientLooks(ids);
      setConfirmDeleteGroupId('');
      await refetch();
    } catch (err) {
      setPageError(err?.response?.data?.error || 'Failed to delete.');
    } finally {
      setDeletingGroupId('');
    }
  };

  const toggleTag = (tag) =>
    setNoteTags((prev) => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);

  const submitNote = async (group) => {
    if (!noteBody.trim()) return;
    const linkLookKey = group.reference?.lookKey || group.generated[0]?.lookKey;
    setNoteSubmitting(true);
    try {
      await createDirectionNote({ body: noteBody.trim(), tags: noteTags, lookId: linkLookKey });
      setNoteOpenGroup('');
      setNoteBody('');
      setNoteTags([]);
    } catch (err) {
      setPageError(err?.response?.data?.error || 'Failed to submit note.');
    } finally {
      setNoteSubmitting(false);
    }
  };

  if (isLoading) return <Spinner T={T} />;
  if (isError) return <ErrorBanner T={T}>{error?.response?.data?.error || 'Failed to load looks.'}</ErrorBanner>;

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: T.textMuted, marginBottom: 6 }}>
          Studio Sivad
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, color: T.text, letterSpacing: '-0.01em', lineHeight: 1 }}>
          Looks
        </div>
        {albums.length > 0 && (
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 6 }}>
            {albums.length} {albums.length === 1 ? 'collection' : 'collections'}
          </div>
        )}
      </div>

      {pageError && (
        <div style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${T.errorBorder}`, background: T.errorBg, color: T.errorText, fontSize: 12, marginBottom: 20 }}>
          {pageError}
        </div>
      )}

      {albums.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
          {albums.map((group, gi) => {
            const pendingCount = group.generated.filter(l => l.latestStatus === 'pending').length;
            const noteOpen = noteOpenGroup === group.shareGroupId;
            const sharedDate = group.sharedAt
              ? new Date(group.sharedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
              : null;

            return (
              <div key={group.shareGroupId}>
                {/* ── Album header ── */}
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: T.textMuted, flexShrink: 0 }}>
                      {String(gi + 1).padStart(2, '0')}
                    </div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: T.text, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {group.sessionName || 'Look Set'}
                    </div>
                    {pendingCount > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: GOLD, display: 'inline-block' }} />
                        <span style={{ fontSize: 10, fontWeight: 700, color: GOLD, letterSpacing: '0.06em' }}>
                          {pendingCount} pending
                        </span>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                    {sharedDate && (
                      <div style={{ fontSize: 11, color: T.textMuted }}>{sharedDate}</div>
                    )}
                    {confirmDeleteGroupId === group.shareGroupId ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 10, color: T.textMuted, letterSpacing: '0.04em' }}>Delete?</span>
                        <button
                          disabled={deletingGroupId === group.shareGroupId}
                          onClick={() => deleteGroup(group)}
                          style={{ padding: '4px 10px', borderRadius: 5, border: 'none', background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.04em', opacity: deletingGroupId === group.shareGroupId ? 0.6 : 1 }}>
                          {deletingGroupId === group.shareGroupId ? '…' : 'Yes'}
                        </button>
                        <button
                          onClick={() => setConfirmDeleteGroupId('')}
                          style={{ padding: '4px 10px', borderRadius: 5, border: `1px solid ${T.border}`, background: 'none', color: T.textMuted, fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                          No
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmDeleteGroupId(group.shareGroupId)}
                        title="Delete this look set"
                        style={{ all: 'unset', cursor: 'pointer', color: T.textMuted, display: 'flex', alignItems: 'center', opacity: 0.5, transition: 'opacity 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.opacity = 1}
                        onMouseLeave={e => e.currentTarget.style.opacity = 0.5}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {/* ── Thin gold rule ── */}
                <div style={{ height: 1, background: isLight ? 'rgba(201,168,76,0.2)' : 'rgba(201,168,76,0.15)', marginBottom: 20 }} />

                {/* ── Reference image ── */}
                {group.reference?.attachmentUrl && (
                  <div style={{ marginBottom: 20, borderRadius: 10, overflow: 'hidden', background: isLight ? '#f0f1f4' : '#111', maxWidth: 340 }}>
                    <img
                      src={group.reference.attachmentUrl}
                      alt="Reference"
                      style={{ width: '100%', maxHeight: 280, objectFit: 'contain', display: 'block' }}
                    />
                    {group.reference.studioNote && (
                      <div style={{ padding: '12px 18px', fontSize: 12, color: T.textSub, fontStyle: 'italic', letterSpacing: '0.01em', borderTop: `1px solid ${T.border}` }}>
                        "{group.reference.studioNote}"
                      </div>
                    )}
                  </div>
                )}

                {/* ── Generated gallery ── */}
                {group.generated.length > 0 && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: T.textMuted, marginBottom: 12 }}>
                      Generated — {group.generated.length} look{group.generated.length !== 1 ? 's' : ''}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10 }}>
                      {group.generated.map((look) => {
                        const busy = submittingId === look.latestApprovalId;
                        const hovered = hoveredLook === look.lookKey;
                        const isPending = look.latestStatus === 'pending';
                        const shotLabel = look.subjectLabel?.split('—').pop()?.trim().replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Look';
                        return (
                          <div
                            key={look.lookKey}
                            style={{ borderRadius: 8, overflow: 'hidden', position: 'relative', background: isLight ? '#f0f1f4' : '#111', cursor: 'zoom-in' }}
                            onMouseEnter={() => setHoveredLook(look.lookKey)}
                            onMouseLeave={() => setHoveredLook('')}
                          >
                            {/* Image */}
                            <img
                              src={look.attachmentUrl}
                              alt={shotLabel}
                              onClick={() => setLightbox({ url: look.attachmentUrl, label: shotLabel, look })}
                              style={{ width: '100%', display: 'block', objectFit: 'contain', maxHeight: 300, minHeight: 160 }}
                            />

                            {/* Status dot — top right always visible */}
                            <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 999, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)' }}>
                              <StatusDot status={look.latestStatus} />
                              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.8)' }}>
                                {STATUS_META[look.latestStatus]?.label || look.latestStatus}
                              </span>
                            </div>

                            {/* Shot type label — bottom left */}
                            <div style={{ position: 'absolute', bottom: isPending && hovered ? 46 : 10, left: 10, fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', transition: 'bottom 0.18s ease' }}>
                              {shotLabel}
                            </div>

                            {/* Action bar — slides up on hover when pending */}
                            {isPending && (
                              <div style={{
                                position: 'absolute', bottom: 0, left: 0, right: 0,
                                display: 'flex',
                                transform: hovered ? 'translateY(0)' : 'translateY(100%)',
                                transition: 'transform 0.18s ease',
                              }}>
                                <button
                                  disabled={busy}
                                  onClick={() => respond(look.latestApprovalId, 'approved')}
                                  style={{ flex: 1, padding: '11px 0', cursor: busy ? 'wait' : 'pointer', border: 'none', background: 'rgba(34,197,94,0.85)', color: '#fff', opacity: busy ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                ><ThumbsUp size={16} /></button>
                                <button
                                  disabled={busy}
                                  onClick={() => respond(look.latestApprovalId, 'needs_revision')}
                                  style={{ flex: 1, padding: '11px 0', fontSize: 16, cursor: busy ? 'wait' : 'pointer', border: 'none', borderLeft: '1px solid rgba(255,255,255,0.1)', background: 'rgba(20,20,20,0.85)', color: 'rgba(255,255,255,0.7)', opacity: busy ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                ><Pencil size={14} /></button>
                                <button
                                  disabled={busy}
                                  onClick={() => respond(look.latestApprovalId, 'rejected')}
                                  style={{ flex: 1, padding: '11px 0', fontSize: 16, cursor: busy ? 'wait' : 'pointer', border: 'none', borderLeft: '1px solid rgba(255,255,255,0.1)', background: 'rgba(239,68,68,0.8)', color: '#fff', opacity: busy ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                ><ThumbsDown size={16} /></button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── Direction note ── */}
                <div style={{ marginTop: 20 }}>
                  {!noteOpen ? (
                    <button
                      onClick={() => { setNoteOpenGroup(group.shareGroupId); setNoteBody(''); setNoteTags([]); }}
                      style={{ all: 'unset', cursor: 'pointer', fontSize: 11, color: T.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700, borderBottom: `1px solid ${T.border}`, paddingBottom: 2, transition: 'color 0.15s' }}
                      onMouseEnter={e => e.target.style.color = GOLD}
                      onMouseLeave={e => e.target.style.color = T.textMuted}
                    >
                      + Direction Note
                    </button>
                  ) : (
                    <div style={{ padding: 16, borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface }}>
                      <textarea
                        placeholder="Share your direction for this look set…"
                        value={noteBody}
                        onChange={(e) => setNoteBody(e.target.value)}
                        rows={3}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 7, border: `1px solid ${T.inputBorder}`, background: T.inputBg, color: T.inputColor, fontSize: 13, resize: 'none', outline: 'none', lineHeight: 1.6, marginBottom: 10 }}
                      />
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 12 }}>
                        {TAGS.map(tag => {
                          const active = noteTags.includes(tag);
                          return (
                            <button key={tag} type="button" onClick={() => toggleTag(tag)}
                              style={{ padding: '3px 10px', borderRadius: 999, fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', border: active ? `1px solid ${GOLD}` : `1px solid ${T.inputBorder}`, background: active ? 'rgba(201,168,76,0.12)' : 'transparent', color: active ? GOLD : T.textMuted, transition: 'all 0.12s' }}>
                              {tag}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setNoteOpenGroup('')}
                          style={{ padding: '7px 16px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', border: `1px solid ${T.inputBorder}`, background: 'none', color: T.textSub, letterSpacing: '0.04em' }}>
                          Cancel
                        </button>
                        <button
                          disabled={noteSubmitting || !noteBody.trim()}
                          onClick={() => submitNote(group)}
                          style={{ padding: '7px 18px', borderRadius: 6, border: `1px solid ${GOLD}`, background: GOLD, color: '#0e0e0e', fontWeight: 700, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: noteSubmitting || !noteBody.trim() ? 'not-allowed' : 'pointer', opacity: noteSubmitting || !noteBody.trim() ? 0.5 : 1 }}>
                          {noteSubmitting ? 'Sending…' : 'Send'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ padding: '60px 0', textAlign: 'center' }}>
          <div style={{ fontSize: 28, opacity: 0.1, marginBottom: 12 }}>���</div>
          <div style={{ fontSize: 13, color: T.textMuted, letterSpacing: '0.04em' }}>No looks have been shared with you yet.</div>
        </div>
      )}

      {/* ── Lightbox ── */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out' }}
        >
          <img
            src={lightbox.url}
            alt={lightbox.label}
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8, display: 'block', boxShadow: '0 32px 80px rgba(0,0,0,0.8)', cursor: 'default' }}
          />
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 16 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)' }}>
              {lightbox.label}
            </span>
            {lightbox.look.latestStatus === 'pending' && (
              <>
                <div style={{ width: 1, height: 14, background: 'rgba(255,255,255,0.15)' }} />
                <button
                  onClick={e => { e.stopPropagation(); respond(lightbox.look.latestApprovalId, 'approved'); setLightbox(null); }}
                  style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: 'rgba(34,197,94,0.85)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ThumbsUp size={16} />
                </button>
                <button
                  onClick={e => { e.stopPropagation(); respond(lightbox.look.latestApprovalId, 'needs_revision'); setLightbox(null); }}
                  style={{ padding: '6px 16px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Pencil size={15} />
                </button>
                <button
                  onClick={e => { e.stopPropagation(); respond(lightbox.look.latestApprovalId, 'rejected'); setLightbox(null); }}
                  style={{ padding: '6px 16px', borderRadius: 6, border: 'none', background: 'rgba(239,68,68,0.8)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ThumbsDown size={16} />
                </button>
              </>
            )}
          </div>
          <div style={{ marginTop: 10, fontSize: 10, color: 'rgba(255,255,255,0.2)', letterSpacing: '0.06em' }}>click anywhere to close</div>
        </div>
      )}
    </div>
  );
}
